import { useState, useEffect } from 'react';
import { User, ModulePermission } from '@/types';
import { usePermissionsConfig, useUpdateUserPermissions, useResetUserPermissions } from '@/hooks/useUsers';
import { getRoleName } from '@/services/users';
import Button from '@/components/ui/Button';
import { Shield, RotateCcw, Save, Loader2 } from 'lucide-react';
import clsx from 'clsx';

interface UserPermissionsPanelProps {
  user: User;
  onClose: () => void;
}

// Iconos para cada módulo
const MODULE_ICONS: Record<ModulePermission, string> = {
  dashboard: '📊',
  products: '📦',
  products_write: '📝',
  products_view_costs: '💲',
  products_edit_prices: '🏷️',
  inventory: '🏬',
  sales: '💰',
  alerts: '🔔',
  reports: '📈',
  users: '👥',
  settings: '⚙️',
  cash_register: '💵',
  finances: '💼',
};

export default function UserPermissionsPanel({ user, onClose }: UserPermissionsPanelProps) {
  const { data: config, isLoading: loadingConfig } = usePermissionsConfig();
  const updatePermissions = useUpdateUserPermissions();
  const resetPermissions = useResetUserPermissions();

  const [selectedPermissions, setSelectedPermissions] = useState<ModulePermission[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Inicializar permisos
  useEffect(() => {
    if (user.effective_permissions) {
      setSelectedPermissions([...user.effective_permissions]);
    }
  }, [user.effective_permissions]);

  // Detectar cambios
  useEffect(() => {
    const original = user.effective_permissions || [];
    const changed =
      selectedPermissions.length !== original.length ||
      selectedPermissions.some(p => !original.includes(p));
    setHasChanges(changed);
  }, [selectedPermissions, user.effective_permissions]);

  const handleTogglePermission = (moduleId: ModulePermission) => {
    setSelectedPermissions(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(p => p !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };

  const handleSave = async () => {
    try {
      await updatePermissions.mutateAsync({
        id: user.id,
        permissions: selectedPermissions,
      });
      onClose();
    } catch (error) {
      console.error('Error updating permissions:', error);
    }
  };

  const handleReset = async () => {
    try {
      await resetPermissions.mutateAsync(user.id);
      // Actualizar estado local con defaults
      if (config) {
        const defaults = config.default_permissions[user.role] || [];
        setSelectedPermissions(defaults);
      }
    } catch (error) {
      console.error('Error resetting permissions:', error);
    }
  };

  if (loadingConfig) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
      </div>
    );
  }

  const defaultPermissions = config?.default_permissions[user.role] || [];
  const isCustomized = user.module_permissions !== null && user.module_permissions !== undefined;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 pb-3 border-b">
        <Shield className="h-5 w-5 text-primary-500" />
        <div>
          <h3 className="font-semibold text-gray-900">Permisos de {user.full_name}</h3>
          <p className="text-sm text-gray-500">
            Rol: {getRoleName(user.role)}
            {isCustomized && (
              <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                Personalizado
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Info sobre defaults */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        <p>
          <strong>Permisos por defecto del rol "{getRoleName(user.role)}":</strong>
        </p>
        <p className="mt-1 text-blue-600">
          {defaultPermissions.map(p => config?.modules.find(m => m.id === p)?.name).filter(Boolean).join(', ') || 'Ninguno'}
        </p>
      </div>

      {/* Lista de módulos */}
      <div className="grid grid-cols-2 gap-2">
        {config?.modules.map(module => {
          const isSelected = selectedPermissions.includes(module.id);
          const isDefault = defaultPermissions.includes(module.id);

          return (
            <label
              key={module.id}
              className={clsx(
                'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                isSelected
                  ? 'bg-primary-50 border-primary-300 shadow-sm'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              )}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleTogglePermission(module.id)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-xl">{MODULE_ICONS[module.id]}</span>
              <div className="flex-1 min-w-0">
                <p className={clsx(
                  'font-medium text-sm',
                  isSelected ? 'text-primary-900' : 'text-gray-700'
                )}>
                  {module.name}
                </p>
                <p className="text-xs text-gray-500 truncate">{module.description}</p>
              </div>
              {isDefault && (
                <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
                  Default
                </span>
              )}
            </label>
          );
        })}
      </div>

      {/* Acciones */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="secondary"
          size="sm"
          onClick={handleReset}
          disabled={resetPermissions.isPending || !isCustomized}
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Restaurar defaults
        </Button>

        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || updatePermissions.isPending}
          >
            {updatePermissions.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
}
